import { Link } from 'react-router';
import { Trophy, ArrowLeft, TrendingUp, Medal, Crown, Award } from 'lucide-react';
import { useLeaderboard } from '@/hooks/use-challenges';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import type { LeaderboardEntry } from '@/types/api';

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return '***';
  const visible = local.slice(0, 2);
  return `${visible}***@${domain}`;
}

function RankIcon({ rank }: { rank: number }) {
  if (rank === 1) return <Crown size={16} className="text-yellow-400" />;
  if (rank === 2) return <Medal size={16} className="text-gray-300" />;
  if (rank === 3) return <Medal size={16} className="text-amber-600" />;
  return <span className="text-xs font-bold text-ink-4 w-4 text-center">{rank}</span>;
}

function LeaderboardRow({ entry, rank }: { entry: LeaderboardEntry; rank: number }) {
  const returnPct =
    entry.metrics?.return_pct ??
    ((entry.peak_equity - entry.starting_capital) / entry.starting_capital) * 100;
  const isPassed = entry.status === 'PASSED';

  return (
    <div className="glass rounded-lg p-4 flex items-center gap-4">
      <div className="flex items-center justify-center w-8">
        <RankIcon rank={rank} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold truncate">
            {entry.user ? maskEmail(entry.user.email) : 'Anonymous'}
          </span>
          {entry.user?.is_founding_trader && (
            <Badge color="default">
              <Award size={10} className="mr-0.5" />
              Founding
            </Badge>
          )}
        </div>
        <div className="text-[10px] text-ink-4 mt-0.5">
          {entry.bot?.symbol || '—'} · {entry.tier} · {entry.bot?.total_trades ?? 0} trades
        </div>
      </div>

      <div className="text-right">
        <div className={`text-sm font-extrabold ${returnPct >= 0 ? 'text-green' : 'text-red'}`}>
          {returnPct >= 0 ? '+' : ''}{returnPct.toFixed(1)}%
        </div>
        <div className="text-[10px] text-ink-4">
          ${entry.peak_equity.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </div>
      </div>

      <Badge color={isPassed ? 'green' : 'blue'}>
        {isPassed ? 'Passed' : 'Active'}
      </Badge>
    </div>
  );
}

export default function ChallengeLeaderboardPage() {
  const { data: entries, isLoading, error } = useLeaderboard(50);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Failed to load leaderboard</p>
        <p className="text-xs text-ink-3">Check that the API server is running.</p>
      </div>
    );
  }

  const all = entries ?? [];
  const passed = all.filter((e) => e.status === 'PASSED');
  const active = all.filter((e) => e.status === 'ACTIVE');

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Back link */}
      <Link
        to="/challenges"
        className="inline-flex items-center gap-1.5 text-xs text-ink-3 hover:text-ink transition-colors"
      >
        <ArrowLeft size={14} />
        My Challenges
      </Link>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Trophy size={24} className="text-orange" />
        <div>
          <h1 className="text-xl font-extrabold tracking-tight">Leaderboard</h1>
          <p className="text-sm text-ink-3 mt-0.5">
            Top challenge performers ranked by return
          </p>
        </div>
      </div>

      {all.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center">
          <TrendingUp size={40} className="mx-auto text-ink-4 mb-3" />
          <h2 className="text-lg font-bold mb-1">No entries yet</h2>
          <p className="text-sm text-ink-3 max-w-md mx-auto">
            Be the first to complete a challenge and claim a spot on the leaderboard.
          </p>
        </div>
      ) : (
        <>
          {/* Passed challenges */}
          {passed.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-ink-3 uppercase tracking-wider mb-3">
                Completed Challenges
              </h2>
              <div className="space-y-2">
                {passed.map((entry, i) => (
                  <LeaderboardRow key={entry.id} entry={entry} rank={i + 1} />
                ))}
              </div>
            </div>
          )}

          {/* Active challenges */}
          {active.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-ink-3 uppercase tracking-wider mb-3">
                Active Challenges
              </h2>
              <div className="space-y-2">
                {active.map((entry, i) => (
                  <LeaderboardRow key={entry.id} entry={entry} rank={passed.length + i + 1} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
